import {
  ALLOWED_TASKS_BY_SINGLE_ENTITY_ORIGIN_TABLE,
  ENDPOINTS,
  ORIGIN_TABLE_MAP,
  TASK_TYPES
} from 'features/daily-plan/constants';
import isNull from 'lodash/isNull';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import moment from 'moment';
import API from 'services/API';
import strings from 'strings';
import {
  ContactEntityMapping,
  ContactMapByEntityType,
  ENTITY_TYPE_ID_MAP,
  EntityType
} from 'UI/constants/entityTypes';
import { nestTernary, toLocalTime } from 'UI/utils';
import { prepareFormToSubmit } from 'UI/utils/forms';

import { FORM_FIELDS_MAP as CALL_TASK_FIELDS } from '../../forms/CallTask/CallTask.constants';
import { FORM_FIELDS_MAP as MARKET_CANDIDATE_FIELDS } from '../../forms/MarketCandidate/MarketCandidate.constants';
import { FORM_FIELDS_MAP as OTHER_TASK_FIELDS } from '../../forms/OtherTask/OtherTask.constants';
import { FORM_FIELDS_MAP as RECRUIT_JOB_ORDER_FIELDS } from '../../forms/RecruitJobOrder/RecruitJobOrder.constants';
import { FORM_FIELDS_MAP as TEAM_FIELDS } from '../../forms/shared/TeamSelectors/TeamSelectors.constants';

import { CALLING_TASKS_ID } from './CreationTaskDrawer.constants';

export const getStartDateTime = formData => {
  if (!formData?.time) {
    return formData?.date?.format('YYYY-MM-DD');
  }

  const combinedDateTime = formData?.date?.clone();

  combinedDateTime.hours(formData?.time?.hours());
  combinedDateTime.minutes(formData?.time?.minutes());
  combinedDateTime.seconds(formData?.time?.seconds());
  combinedDateTime.milliseconds(formData?.time?.milliseconds());

  const utcDateTime = combinedDateTime.utc();

  const startDatetime = utcDateTime.toISOString();

  return startDatetime;
};

const getEntityByTask = ({ formData }) => {
  if (formData.taskType?.id === TASK_TYPES.recruitJobOrder) {
    return formData.reachToName?.entityType;
  }

  if (formData.taskType?.id === TASK_TYPES.marketCandidate) {
    const keyToExtractEntity =
      formData.reachTo === EntityType.HiringAuthority ? 'entityType' : 'type';
    return formData.reachToName?.[keyToExtractEntity];
  }

  if (formData.taskType?.id === TASK_TYPES.other) {
    return formData.reachTo === EntityType.Contact
      ? ContactEntityMapping[formData.reachToName?.role_id]
      : formData.reachToName?.type;
  }

  return null;
};

export const formatRegularTaskData = ({ formData, formFieldsMap }) => {
  const entity = getEntityByTask({ formData });

  const fieldsToExclude = ['date', 'time', 'reachTo', 'reachToName', 'tasksQuery', 'team'];
  const commonData = omit(prepareFormToSubmit(formData, formFieldsMap), fieldsToExclude);

  const result = {
    ...commonData,
    startDatetime: getStartDateTime(formData)
  };

  if (formData.tasksQuery) {
    const {
      params = '',
      queryName = '',
      exclude = [],
      searchProjectId,
      bulkEmailId
    } = formData.tasksQuery;

    const queryParams = new URLSearchParams(params);
    const queryResult = {
      exclude,
      query: Object.fromEntries(queryParams)
    };

    if (searchProjectId) {
      queryResult.searchProjectId = Number(searchProjectId);
    }
    if (bulkEmailId) {
      queryResult.bulkEmailId = Number(bulkEmailId);
    }

    result[queryName] = queryResult;
  } else if (formData.items?.length && !formData.tasksQuery) {
    result.items = formData.items;
  } else {
    result.item = {
      entityId: formData.reachToName?.id || null,
      entityTypeId: ENTITY_TYPE_ID_MAP[entity] || null
    };
  }

  return result;
};

const createCallItems = data => {
  const entities = [EntityType.Candidate, EntityType.HiringAuthority];
  const { interview } = data;

  const isExistingTask = !!data.id;

  return entities
    .filter(entity => data[`isSelected-${entity}`])
    .map(entity => {
      const entityId = isExistingTask
        ? data.entityId
        : nestTernary(
            entity === EntityType.Candidate,
            interview.candidateId,
            interview.hiringAuthorityId
          );
      return {
        comment: data[`comments-${entity}`],
        startDatetime: getStartDateTime({
          date: data[`date-${entity}`],
          time: data[`time-${entity}`]
        }),
        entityId,
        entityTypeId: ENTITY_TYPE_ID_MAP[entity]
      };
    });
};

export const formatCallingTaskData = ({ formData }) => {
  const formFieldsMap = { ...CALL_TASK_FIELDS, ...TEAM_FIELDS };
  const commonData = omit(prepareFormToSubmit(formData, formFieldsMap), [
    CALL_TASK_FIELDS.Comments.key,
    CALL_TASK_FIELDS.AtLeastOneContact.key,
    CALL_TASK_FIELDS.IsCandidateSelected.key,
    CALL_TASK_FIELDS.IsHiringAuthSelected.key,
    CALL_TASK_FIELDS.TaskId.key,
    CALL_TASK_FIELDS.StartDateTime.key,
    CALL_TASK_FIELDS.EntityId.key,
    TEAM_FIELDS.Team.key
  ]);

  const isExistingTask = !!formData.id;

  const itemKey = isExistingTask ? 'item' : 'items';
  const items = createCallItems(formData);
  if (!isExistingTask) {
    const now = toLocalTime(moment().format());
    items.forEach(item => {
      const selectedDate = item?.startDatetime ? moment(item?.startDatetime) : null;
      const isValidDate = selectedDate ? selectedDate.isSameOrAfter(now) : false;

      if (!isValidDate) throw new Error(strings.dailyPlan.drawer.form.errors.pastDates);
    });
  }

  const result = {
    ...commonData,
    [itemKey]: isExistingTask ? items[0] : items
  };

  return result;
};

export const formatCallingTaskToPreload = values => {
  const entity = values['isSelected-candidate']
    ? EntityType.Candidate
    : nestTernary(values['isSelected-hiringAuthority'], EntityType.HiringAuthority, null);

  const callDateTime = moment(values.callDateTime);
  const callDefaultValues = {
    ...values,
    [`comments-${entity}`]: values.comments,
    [`date-${entity}`]: callDateTime,
    [`time-${entity}`]: callDateTime
  };

  return callDefaultValues;
};

const callTaskHandler = async formData => {
  const dataToSend = formatCallingTaskData({ formData });

  const isExistingTask = !!formData.id;

  const endpoint = isExistingTask ? ENDPOINTS.task.replace(':id', formData.id) : ENDPOINTS.tasks;
  const request = isExistingTask ? API.patch : API.post;

  return request(endpoint, dataToSend);
};

const taskHandler = formFieldsMap => {
  return async formData => {
    const finalFormFieldsMap = { ...formFieldsMap, ...TEAM_FIELDS };
    const dataToSend = formatRegularTaskData({
      formData,
      formFieldsMap: finalFormFieldsMap
    });

    const isExistingTask = !!formData.id;

    const endpoint = isExistingTask ? ENDPOINTS.task.replace(':id', formData.id) : ENDPOINTS.tasks;
    const request = isExistingTask ? API.patch : API.post;

    const finalDataToSend =
      isExistingTask && formData?.taskType?.id === TASK_TYPES.other
        ? dataToSend
        : omitBy(dataToSend, isNull);

    return request(endpoint, finalDataToSend);
  };
};

export const TASK_CONFIG = {
  [TASK_TYPES.recruitJobOrder]: {
    handler: taskHandler(RECRUIT_JOB_ORDER_FIELDS)
  },
  [TASK_TYPES.marketCandidate]: {
    handler: taskHandler(MARKET_CANDIDATE_FIELDS)
  },
  [TASK_TYPES.debriefCall]: {
    handler: callTaskHandler
  },
  [TASK_TYPES.preparationCall]: {
    handler: callTaskHandler
  },
  [TASK_TYPES.closingCall]: {
    handler: callTaskHandler
  },
  [TASK_TYPES.other]: {
    handler: taskHandler(OTHER_TASK_FIELDS)
  }
};

export const getPrefillForm = ({ taskTypeId, entityData, entityType, setValue }) => {
  const { id, name } = entityData;

  const prefillFormMap = {
    [TASK_TYPES.recruitJobOrder]: () => {
      setValue(RECRUIT_JOB_ORDER_FIELDS.ReachToEntity.key, EntityType.Candidate);

      if (entityType === EntityType.Candidate || entityType === EntityType.Contact) {
        setValue(RECRUIT_JOB_ORDER_FIELDS.ReachToName.key, {
          id,
          fullName: name,
          entityType
        });
      } else {
        setValue(RECRUIT_JOB_ORDER_FIELDS.Position.key, {
          id,
          title: name
        });
      }
    },

    [TASK_TYPES.marketCandidate]: () => {
      const isHA = entityType === EntityType.HiringAuthority;
      const isCompany = entityType === EntityType.Company;
      const isHiringAuthorityProspect = entityType === EntityType.Contact;

      const finalEntityType = isHiringAuthorityProspect ? EntityType.HiringAuthority : entityType;

      if (isHA || isCompany || isHiringAuthorityProspect) {
        const nameKey = isHA || isHiringAuthorityProspect ? 'fullName' : 'name';
        const entityTypeKey = isCompany ? 'type' : 'entityType';

        setValue(MARKET_CANDIDATE_FIELDS.ReachToEntity.key, finalEntityType);
        setValue(MARKET_CANDIDATE_FIELDS.ReachToName.key, {
          id,
          [nameKey]: name,
          [entityTypeKey]: entityType
        });
      } else {
        setValue(MARKET_CANDIDATE_FIELDS.CandidateToMarket.key, {
          id,
          title: name
        });
      }
    },

    [TASK_TYPES.other]: () => {
      const isContactOrHA =
        entityType === EntityType.Contact || entityType === EntityType.HiringAuthority;

      const nameKey = entityType === EntityType.Candidate ? 'title' : 'name';

      const basePayload = {
        id,
        [nameKey]: name,
        entityType,
        type: entityType,
        role_id: ContactMapByEntityType[entityType]
      };

      if (entityType !== EntityType.Joborder) {
        setValue(
          OTHER_TASK_FIELDS.ReachToEntity.key,
          isContactOrHA ? EntityType.Contact : entityType
        );

        setValue(OTHER_TASK_FIELDS.ReachToName.key, {
          ...basePayload,
          ...(isContactOrHA && { full_name: name })
        });
      }
    }
  };

  return prefillFormMap[taskTypeId];
};

export const isCallingTask = taskTypeId => CALLING_TASKS_ID.includes(taskTypeId);

export const getAllowedTasks = table => {
  if (!table) {
    return null;
  }

  const isMultiEntityTable = [
    ORIGIN_TABLE_MAP.Contacts,
    ORIGIN_TABLE_MAP.SearchProjectPreview,
    ORIGIN_TABLE_MAP.BulkStats
  ].includes(table);

  if (isMultiEntityTable) {
    return [TASK_TYPES.recruitJobOrder, TASK_TYPES.marketCandidate, TASK_TYPES.other];
  }

  return ALLOWED_TASKS_BY_SINGLE_ENTITY_ORIGIN_TABLE[table];
};
